/* ==========================================================================
   <infinite-scroll> — loads the next page of products as the shopper nears
   the bottom of a collection or search grid.

   Liquid renders the element only when there IS a next page, carrying
   paginate.next.url (which keeps any filter and sort parameters). The
   numbered pagination stays in the markup: it is what search engines crawl,
   and it comes back if a fetch fails, so the grid can never dead-end.

   Each page is fetched through the Section Rendering API, so the new cards
   are the same Liquid as the first page. A filter change replaces the whole
   results block, element included; the new element starts itself with the
   filtered next URL, so no re-initialisation hook is needed.
   ========================================================================== */
(function () {
  'use strict';

  class InfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.next = this.dataset.next;
      this.grid = this.parentElement && this.parentElement.querySelector('.products-grid');
      this.pagination = this.parentElement && this.parentElement.querySelector('.pagination');
      if (!this.next || !this.grid || !('IntersectionObserver' in window)) return;

      // Script is in charge now: the numbered links step aside.
      if (this.pagination) this.pagination.hidden = true;
      this.loading = false;

      // Start well before the end so the next row is usually there already.
      this.observer = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) this.load();
      }.bind(this), { rootMargin: '900px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    load() {
      if (this.loading || !this.next) return;
      this.loading = true;
      this.classList.add('is-loading');

      var url = this.next + (this.next.indexOf('?') === -1 ? '?' : '&') +
        'section_id=' + encodeURIComponent(this.dataset.section);

      fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fragment = document.createDocumentFragment();
          doc.querySelectorAll('.products-grid > li').forEach(function (item) {
            fragment.appendChild(document.importNode(item, true));
          });
          this.grid.appendChild(fragment);

          var nextElement = doc.querySelector('infinite-scroll');
          this.next = nextElement ? nextElement.dataset.next : null;

          // Wishlist hearts on the new cards (theme.js paints on this event).
          document.dispatchEvent(new CustomEvent('shopify:section:load'));

          this.loading = false;
          this.classList.remove('is-loading');

          if (!this.next) {
            this.observer.disconnect();
            this.remove();
            return;
          }
          // An observer only reports CHANGES. If the sentinel is still in
          // range after the append (a short page, a tall screen), re-observing
          // makes it report again instead of stalling.
          this.observer.unobserve(this);
          this.observer.observe(this);
        }.bind(this))
        .catch(function () {
          // Hand back to the numbered pagination rather than leave the
          // shopper stuck at the end of what loaded.
          this.loading = false;
          this.classList.remove('is-loading');
          if (this.observer) this.observer.disconnect();
          if (this.pagination) this.pagination.hidden = false;
        }.bind(this));
    }
  }

  if (!customElements.get('infinite-scroll')) customElements.define('infinite-scroll', InfiniteScroll);
})();
